import { useState, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { paymentService } from '../services/paymentService';

export type Step = 'amount' | 'payment' | 'processing' | 'success';
export type Tab = 'top-up' | 'withdraw' | 'history';

const MIN_AMOUNT = 1000;
const MAX_AMOUNT = 300000;
const MAX_WALLET_BALANCE = 1000000;

export const useCashInLogic = (isOpen: boolean) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('top-up');
  const [step, setStep] = useState<Step>('amount');
  const [amount, setAmount] = useState<string>('');
  const [selectedPayment, setSelectedPayment] = useState<'MMQR' | 'APLUS' | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cash Out State
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawPhone, setWithdrawPhone] = useState('');

  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [paymentQrCode, setPaymentQrCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [pushNotiLink, setPushNotiLink] = useState<string | null>(null);

  // Polling ref to clear interval
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep('amount');
      setAmount('');
      setSelectedPayment(null);
      setShowQR(false);
      setError(null);
      setActiveTab('top-up');
      setWithdrawAmount('');
      setWithdrawPhone('');
      setTransactionId(null);
      setPaymentQrCode(null);
      setPushNotiLink(null);
      setIsLoading(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [isOpen]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    const formattedValue = rawValue ? parseInt(rawValue, 10).toLocaleString() : '';
    setAmount(formattedValue);

    const numValue = parseInt(rawValue || '0', 10);
    if (rawValue && (numValue < MIN_AMOUNT || numValue > MAX_AMOUNT)) {
      setError(`Limit: ${MIN_AMOUNT.toLocaleString()} - ${MAX_AMOUNT.toLocaleString()} MMK`);
    } else {
      setError(null);
    }
  };

  const handleNextToPayment = () => {
    const numValue = parseInt(amount.replace(/,/g, ''), 10);
    const currentBalance = user?.balances?.MMK?.available || user?.walletBalance || 0;

    if (!amount || isNaN(numValue)) {
      setError('Please enter a valid amount');
      return;
    }
    if (numValue < MIN_AMOUNT || numValue > MAX_AMOUNT) {
      setError(`Limit: ${MIN_AMOUNT.toLocaleString()} - ${MAX_AMOUNT.toLocaleString()} MMK`);
      return;
    }

    // Check wallet max balance constraint
    if (currentBalance >= MAX_WALLET_BALANCE) {
      setError(
        `Wallet limit reached (${MAX_WALLET_BALANCE.toLocaleString()} MMK). Cannot add more funds.`,
      );
      return;
    }
    if (currentBalance + numValue > MAX_WALLET_BALANCE) {
      setError(
        `Total balance cannot exceed ${MAX_WALLET_BALANCE.toLocaleString()} MMK. You can add up to ${(MAX_WALLET_BALANCE - currentBalance).toLocaleString()} MMK.`,
      );
      return;
    }

    setStep('payment');
  };

  const handlePaymentSelect = async (method: 'MMQR' | 'APLUS') => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    setSelectedPayment(method);
    setStep('processing');
    setIsLoading(true);
    setError(null);
    setPushNotiLink(null);

    const numAmount = parseInt(amount.replace(/,/g, ''), 10);

    try {
      const response = await paymentService.createTopup({
        paymentMethod: method === 'MMQR' ? 'mm_qr' : 'aplus',
        amount: numAmount,
        currency: 'MMK',
        description: 'Wallet top-up',
      });

      if (response.success) {
        setTransactionId(response.data.transactionId);
        setPaymentQrCode(response.data.paymentQrCode || response.data.qrCode || null);

        if (method === 'APLUS' && response.data.pushNotiLink) {
          setPushNotiLink(response.data.pushNotiLink);
        }

        setShowQR(true);
      } else {
        setError(response.message || 'Failed to initiate payment');
        setStep('payment');
      }
    } catch (err) {
      console.error('Payment creation failed', err);
      setError('Failed to initiate payment. Please try again.');
      setStep('payment');
    } finally {
      setIsLoading(false);
    }
  };

  const pollingStartTimeRef = useRef<number | null>(null);

  // Poll for status when transactionId is set
  useEffect(() => {
    if (!transactionId || step !== 'processing') {
      pollingStartTimeRef.current = null;
      return;
    }

    // Set start time if not set
    if (!pollingStartTimeRef.current) {
      pollingStartTimeRef.current = Date.now();
    }

    const pollStatus = async () => {
      try {
        // Timeout check (5 minutes)
        if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current > 300000) {
          setError('Transaction verification timed out. Please check your history.');
          setTransactionId(null);
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          return;
        }

        const response = await paymentService.checkTopupStatus(transactionId);

        // API 304 Not Modified optimization - skip state updates
        if (response._status === 304) {
          return;
        }

        if (response.success && response.data.status === 'COMPLETED') {
          setStep('success');
          // Update transactionId to walletTransactionId if available for display
          if (response.data.walletTransactionId) {
            setTransactionId(response.data.walletTransactionId);
          }
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        } else if (response.success && response.data.status === 'FAILED') {
          setError('Transaction failed');
          setTransactionId(null);
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          // Optionally move back to payment or show error state
        }
      } catch (err) {
        console.error('Polling status error', err);
      }
    };

    // Initial check
    pollStatus();

    // Set interval
    pollIntervalRef.current = setInterval(pollStatus, 3000); // Check every 3 seconds

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [transactionId, step]);

  return {
    activeTab,
    setActiveTab,
    step,
    setStep,
    amount,
    setAmount,
    selectedPayment,
    showQR,
    error,
    setError,
    withdrawAmount,
    setWithdrawAmount,
    withdrawPhone,
    setWithdrawPhone,
    handleAmountChange,
    handleNextToPayment,
    handlePaymentSelect,
    isLoading,
    paymentQrCode,
    transactionId,
    pushNotiLink,
    setPushNotiLink, // Expose to allow closing iframe
  };
};
